import styled, { css } from 'styled-components';
import {
  mq,
  getGridSpan,
  getGridColumns,
  setGridColumns,
  gridContainer,
  getGridRows,
  getSpace,
  getColor
} from '../../../stylesheets/tools';

const StyledArticleTeaser = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: ${getSpace('l')};

  ${mq('m')} {
    width: ${props => (props.size === 's' && !props.fullWidth ? getGridSpan(6) : '100%')};
  }

  .articleTeaser__tags {
    margin: 15px 0;
  }

  .articleTeaser__categories {
    justify-content: flex-end;
    flex-wrap: wrap;
    margin: 20px 0;
    padding-top: 10px;

    div {
      margin-bottom: 5px;
    }

    div:last-of-type {
      margin-right: 0;
    }
  }

  .articleTeaser__headlines {
    margin-top: 30px;
    display: flex;
  }

  .articleTeaser__icon {
    flex-shrink: 0;
    margin-right: ${getSpace('xxs')};
    fill: #000;
    color: ${props =>
      props.theme.siteTheme.primary !== '#fff' ? props.theme.siteTheme.primary : '#000'};
  }

  .articleTeaser__image-wrapper {
    position: relative;
    padding-bottom: ${props => (props.imageFormat === 'portrait' ? '133.333%' : '75%')};
    height: 0;
    background: ${getColor('lightGray')};
    animation: loadingBg 1s linear infinite alternate;

    img {
      position: absolute;
      height: 100%;
      width: 100%;
    }
  }

  .articleTeaser__copy {
    position: relative;
    margin-top: 20px;

    p {
      margin: 0;
    }
  }

  .articleTeaser__arrow {
    margin-top: ${getSpace('xs')};

    ${mq('m')} {
      opacity: 0;
      visibility: hidden;
    }

    svg {
      width: 60px;
      height: 20px;
    }
  }

  .articleTeaser__link {
    ${gridContainer()}
    ${setGridColumns('1fr', 12)}
    grid-gap: 10px;
    grid-row-gap: 0;

    ${mq('m')} {
      ${props => props.size === 's' && setGridColumns('1fr', 6)};

      &:hover .articleTeaser__arrow,
      &:focus .articleTeaser__arrow {
        opacity: 1;
        visibility: visible;
      }
    }
  }

  ${props =>
    props.size === 's' &&
    css`
      .articleTeaser__categories {
        border-top: 3px solid black;
        ${getGridColumns([1, 11], { m: [1, 6] })}
        ${getGridRows(1)};
      }

      .articleTeaser__headlines {
        ${getGridColumns([2, 13], { m: [1, 6] })}
        ${getGridRows(2)};

        ${mq('m')} {
          margin-left: 40px;
        }
      }

      .articleTeaser__copy {
        ${getGridColumns([2, 13], { m: [1, 7] })}
        ${getGridRows(3)};

        ${mq('m')} {
          padding-right: 10px;
          margin-left: 40px;
        }
      }

      ${props.imageFormat === 'portrait' &&
        css`
          .articleTeaser__image {
            ${getGridColumns(props.alignment === 'left' ? [1, 9] : [5, 13], { m: [1, 6] })}
            ${getGridRows(2)};
          }

          .articleTeaser__categories {
            ${getGridColumns(props.alignment === 'left' ? [1, 9] : [5, 13], { m: [1, 6] })}
            ${getGridRows(1)};
            border: 0;
          }

          .articleTeaser__headlines {
            ${getGridColumns(null, { m: [1, 6] })};
            ${getGridRows(3)};
          }

          .articleTeaser__copy {
            ${getGridColumns(null, { m: [1, 7] })};
            ${getGridRows(4)};
          }
        `}

      ${props.imageFormat === 'landscape' &&
        css`
          .articleTeaser__image {
            ${getGridColumns(props.alignment === 'left' ? [1, 11] : [3, 13], { m: [1, 6] })}
            ${getGridRows(2)}
          }

          .articleTeaser__categories {
            ${getGridRows(1)};
            border: 0;
          }

          .articleTeaser__headlines {
            ${getGridRows(3)};
          }

          .articleTeaser__copy {
            ${getGridRows(4)};
          }
        `}
    `}

  ${props =>
    props.size === 'm' &&
    css`
      .articleTeaser__tags {
        display: inline-block;
      }

      .articleTeaser__categories {
        ${getGridRows(1)}
      }

      .articleTeaser__categories,
      .articleTeaser__headlines {
        ${
          props.hasCopy === null
            ? getGridColumns([2, 12], { m: [2, 10] })
            : getGridColumns([2, 12], { m: [1, 5] })
        }

        h2, h3 {
          hyphens: auto;
        }
      }

      .articleTeaser__headlines {
        ${getGridRows(2)}
        text-align: right;
        display: block;
      }

      .articleTeaser__icon {
        margin-right: 0;
        margin-bottom: ${getSpace('xxs')};
      }
      
      .articleTeaser__border {
        ${getGridRows(2, { m: [2, 4] })}
        ${
          !props.hasCopy
            ? getGridColumns([12, 13], { m: [10, 11] })
            : getGridColumns([12, 13], { m: [5, 6] })
        }
        border-right: 3px solid black;
        margin: ${props.hasCopy ? '30px 30px 80px 20px' : '30px 30px 50px 20px'};
      }
      

      .articleTeaser__copy {
        ${getGridColumns([2, 13], { m: [6, 12] })}
        ${getGridRows(4, { m: [3, 4] })}
      
        ${mq('m')} {
          bottom: 40px;
        }
      }

      ${props.imageFormat === 'portrait' &&
        css`
          .articleTeaser__categories {
            ${mq('m')} {
              margin-top: 25px;
            }
          }

          .articleTeaser__image {
            ${getGridColumns([3, 13], { m: [5, 10] })}
            ${getGridRows(3, { m: [1, 4] })}
            margin-top: 20px;
          }

          .articleTeaser__categories,
          .articleTeaser__headlines {
            ${mq('m')} {
              margin-right: 15px;
            }
          }

          .articleTeaser__copy {
            ${getGridColumns(null, { m: [10, 13] })}
            ${getGridRows(4, { m: [1, 4] })}

            ${mq('m')} {
              align-self: end;
              bottom: -60px;
              margin-left: 15px;
            }
          }
        `}

      ${props.imageFormat === 'landscape' &&
        css`
          .articleTeaser__categories {
            ${mq('m')} {
              margin-top: 25px;
            }
          }

          .articleTeaser__categories,
          .articleTeaser__headlines {
            ${mq('m')} {
              margin-right: 15px;
            }
          }

          .articleTeaser__headlines {
            ${getGridRows(2, { m: [2, 4] })}
          }

          .articleTeaser__image {
            ${getGridColumns([1, 13], { m: [5, 11] })}
            ${getGridRows(3, { m: [1, 3] })}
            margin-top: 20px;
          }

          .articleTeaser__copy {
            ${getGridColumns(null, { m: [7, 13] })}
            ${getGridRows(4, { m: [3, 4] })}

            ${mq('m')} {
              bottom: 0;
            }
          }
        `}
    `}
`;

export default StyledArticleTeaser;
